import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { Landing } from './pages/Landing';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { Onboarding } from './pages/Onboarding';
import { ConfirmSpot } from './pages/ConfirmSpot';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { ChangePassword } from './pages/ChangePassword';
import { Dashboard } from './pages/Dashboard';
import { Players } from './pages/Players';
import { Sessions } from './pages/Sessions';
import { Waivers } from './pages/Waivers';
import { ProgramRules } from './pages/ProgramRules';
import { AdminDashboard } from './pages/AdminDashboard';
import { AdminSessions } from './pages/AdminSessions';
import { AdminLocations } from './pages/AdminLocations';
import { AdminFinancials } from './pages/AdminFinancials';
import { AdminDiscountCodes } from './pages/AdminDiscountCodes';
import { AdminTemplates } from './pages/AdminTemplates';
import { AdminUsers } from './pages/AdminUsers';
import { AdminSettings } from './pages/AdminSettings';
import { AdminPlayers } from './pages/AdminPlayers';
import { AdminExports } from './pages/AdminExports';
import { AdminCredits } from './pages/AdminCredits';
import { AdminParentsManagement } from './pages/AdminParentsManagement';
import { AdminAnalytics } from './pages/AdminAnalytics';
import { AdminRefunds } from './pages/AdminRefunds';
import { StaffDashboard } from './pages/StaffDashboard';
import { StaffResources } from './pages/StaffResources';
import { QRScanner } from './pages/QRScanner';
import { PaymentSuccess } from './pages/PaymentSuccess';
import { StaffRoster } from './pages/StaffRoster';
import { MyQRCodes } from './pages/MyQRCodes';
import { OpenRunTracker } from './pages/OpenRunTracker';
import { Terms } from './pages/Terms';
import { Privacy } from './pages/Privacy';
import { AuthCallback } from './components/AuthCallback';
import { ProtectedRoute } from './components/ProtectedRoute';
import { LoadingSpinner } from './components/ui/loading-spinner';

function App() {
  return (
    <Router>
      <div className="App min-h-screen bg-background text-foreground overflow-x-hidden">
        <AppRoutes />
        <Toaster position="top-right" />
      </div>
    </Router>
  );
}

function AppRoutes() {
  const location = useLocation();
  
  // Check URL fragment (not query params) for session_id
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/confirm-spot" element={<ConfirmSpot />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      
      {/* Password Change - Protected */}
      <Route path="/change-password" element={
        <ProtectedRoute allowedRoles={['parent', 'staff', 'admin']}>
          <ChangePassword />
        </ProtectedRoute>
      } />
      
      {/* Parent Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={['parent']}>
          <Dashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/players" element={
        <ProtectedRoute allowedRoles={['parent']}>
          <Players />
        </ProtectedRoute>
      } />
      
      <Route path="/sessions" element={
        <ProtectedRoute allowedRoles={['parent']}>
          <Sessions />
        </ProtectedRoute>
      } />
      
      <Route path="/my-qr" element={
        <ProtectedRoute allowedRoles={['parent']}>
          <MyQRCodes />
        </ProtectedRoute>
      } />
      
      <Route path="/waivers" element={
        <ProtectedRoute allowedRoles={['parent']}>
          <Waivers />
        </ProtectedRoute>
      } />
      
      <Route path="/payment-success" element={
        <ProtectedRoute allowedRoles={['parent']}>
          <PaymentSuccess />
        </ProtectedRoute>
      } />
      
      {/* Admin Routes - Admin Only */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/sessions" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminSessions />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/locations" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminLocations />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/financials" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminFinancials />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/discount-codes" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminDiscountCodes />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/templates" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminTemplates />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/users" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminUsers />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/settings" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminSettings />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/players" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminPlayers />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/exports" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminExports />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/credits" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminCredits />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/parents" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminParentsManagement />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/analytics" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminAnalytics />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/refunds" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminRefunds />
        </ProtectedRoute>
      } />
      
      {/* Staff Routes */}
      <Route path="/staff" element={
        <ProtectedRoute allowedRoles={['staff']}>
          <StaffDashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/staff/roster" element={
        <ProtectedRoute allowedRoles={['admin', 'staff']}>
          <StaffRoster />
        </ProtectedRoute>
      } />
      
      <Route path="/staff/resources" element={
        <ProtectedRoute allowedRoles={['admin', 'staff']}>
          <StaffResources />
        </ProtectedRoute>
      } />
      
      <Route path="/scan" element={
        <ProtectedRoute allowedRoles={['admin', 'staff']}>
          <QRScanner />
        </ProtectedRoute>
      } />
      
      {/* Program Rules - All authenticated users */}
      <Route path="/rules" element={
        <ProtectedRoute allowedRoles={['parent', 'staff', 'admin']}>
          <ProgramRules />
        </ProtectedRoute>
      } />
      
      {/* Open Run Tracker - Admin and Staff */}
      <Route path="/open-run" element={
        <ProtectedRoute allowedRoles={['admin', 'staff']}>
          <OpenRunTracker />
        </ProtectedRoute>
      } />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;